import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Heart } from "lucide-react";
import { cn } from "../../lib/utils";
import logoImg from "../image/assets/mrjoe-logo-light.svg";
import { getSessionData, removeSessionData } from "../../lib/global_function";
import LoginApi from "../../login/loginApi";
import PhotoApi from "../../photo/photoApi";
import { Avatar, AvatarImage } from "../ui/avatar";

export default function ProtectedHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [logoPhotoLink, setLogoPhotoLink] = useState<string>('');
  const [accountPhotoLink, setAccountPhotoLink] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const closeMenu = () => setIsMenuOpen(false);

  // Load session data
  useEffect(() => {
    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    if (sessionData?.company_name) setCompanyName(sessionData.company_name);
    if (sessionData?.company_code) setCompanyCode(sessionData.company_code);
    if (sessionData?.account_id) setAccountId(sessionData.account_id);
  }, []);

  // Fetch company logo
  useEffect(() => {
    const fetchPhotoLink = async () => {
      if (companyCode) {
        try {
          const links = await PhotoApi.getPhotoLinks('company', 'small', companyCode);
          if (links && links.length > 0) setLogoPhotoLink(links[0].url);
        } catch (error) {
          // Silent error
        }
      }
    };
    fetchPhotoLink();
  }, [companyCode]);

  // Fetch account photo
  useEffect(() => {
    const fetchAccountPhotoLink = async () => {
      if (accountId) {
        try {
          const links = await PhotoApi.getPhotoLinks('account', 'small', accountId);
          if (links && links.length > 0) setAccountPhotoLink(links[0].url);
        } catch (error) {
          // Silent error
        }
      }
    };
    fetchAccountPhotoLink();
  }, [accountId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      removeSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
      await LoginApi.logout();
      navigate('/');
    } catch (error) {
      // Silent error
    }
  };

  const handleAccountSettings = () => {
    navigate('/main/account/settings');
    setIsDropdownOpen(false);
  };

  const handleAccountProfile = () => {
    navigate('/main/account');
    setIsDropdownOpen(false);
  };

  const handleDonation = () => {
    navigate('/main/account/donation/create');
  };

  return (
    <header className="bg-page-gray-soft border-b border-gray-200 w-full shadow-sm">
      <div className="container-safe grid h-14 grid-cols-[auto_1fr_auto] items-center gap-4">
        {/* Logo */}
        <Link to="/main" className="flex items-center">
          <img src={logoImg} alt="MrJoe.pro logo" className="h-10 w-auto" />
        </Link>

        {/* Company Name & Logo - Center (hidden on mobile) */}
        <div className="justify-self-center max-md:hidden">
          {companyName && (
            <Link
              to="/main/company"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <span className="text-base font-medium text-primary ">
                Welcome {companyName}
              </span>
              {logoPhotoLink && (
                <img
                  src={logoPhotoLink}
                  alt="Company Logo"
                  className="h-6 w-auto object-contain"
                />
              )}
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(true)}
          className="cursor-pointer justify-self-end p-1 md:hidden"
        >
          <Menu className="size-8 text-foreground" />
        </button>

        {/* Desktop: Donation Icon + My Account Dropdown */}
        <div className="flex items-center gap-4 justify-self-end max-md:hidden">
          {/* Donation Icon Link */}
          <button
            onClick={handleDonation}
            className="group flex items-center justify-center p-2 rounded-full hover:bg-pink-50 transition-all duration-300"
            title="Support Us"
          >
            <Heart 
              className="size-5 text-pink-500 fill-pink-500 group-hover:text-pink-600 group-hover:fill-pink-600 group-hover:scale-110 transition-all duration-300" 
            />
          </button>

          {/* My Account Dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              <Avatar>
                <AvatarImage src={accountPhotoLink || '/blank_portait.png'} />
              </Avatar>
              <span className="text-sm font-medium text-primary">My Account</span>
              <ChevronDown className={cn("size-4 transition-transform", isDropdownOpen && "rotate-180")} />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 bg-primary rounded-lg shadow-lg border-2 border-primary-hover min-w-[160px] py-2 z-50">
                {accountId ? (
                  <>
                    <button
                      onClick={handleAccountProfile}
                      className="w-full text-left px-4 py-2 text-sm text-white hover:bg-primary-hover transition-colors"
                    >
                      Profile
                    </button>
                    <button
                      onClick={handleAccountSettings}
                      className="w-full text-left px-4 py-2 text-sm text-white hover:bg-primary-hover transition-colors"
                    >
                      Settings
                    </button>
                    <div className="border-t border-primary-hover my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-white hover:bg-primary-hover transition-colors"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleLogin}
                      className="w-full text-left px-4 py-2 text-sm text-white hover:bg-primary-hover transition-colors"
                    >
                      Login
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-white hover:bg-primary-hover transition-colors"
                    >
                      Logout
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          "bg-primary fixed inset-x-0 top-0 z-50 h-[60dvh] text-white",
          "transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none",
          isMenuOpen ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0"
        )}
      >
        <div>
          {/* Mobile Menu Header */}
          <div className="flex h-14 items-center justify-between border-b border-white/30 px-4">
            <img src={logoImg} alt="MrJoe.pro logo" className="h-10 w-auto brightness-0 invert" />
            <button onClick={closeMenu} className="cursor-pointer p-1">
              <X className="size-8" />
            </button>
          </div>

          {/* Mobile User Info */}
          <div className="flex items-center justify-center gap-3 py-6 border-b border-white/30">
            <img
              src={accountPhotoLink || '/blank_portait.png'}
              alt="Profile"
              className="size-12 rounded-full object-cover border-2 border-white"
            />
            <div className="flex flex-col">
              <span className="text-lg font-semibold">My Account</span>
              {companyName && <span className="text-sm opacity-90">{companyName}</span>}
            </div>
          </div>

          {/* Mobile Menu Items */}
          <nav className="mt-6">
            <ul className="flex flex-col items-center gap-6 text-lg font-semibold">
              {/* Donation Link for Mobile */}
              <li>
                <button
                  onClick={() => {
                    handleDonation();
                    closeMenu();
                  }}
                  className="flex items-center gap-2 rounded-md px-4 py-2 focus:ring-4 focus:ring-white/30 focus:outline-none text-pink-300 hover:text-pink-200"
                >
                  <Heart className="size-5" fill="currentColor" />
                  Support Us
                </button>
              </li>
              {accountId ? (
                <>
                  <li>
                    <button
                      onClick={() => {
                        handleAccountProfile();
                        closeMenu();
                      }}
                      className="rounded-md px-4 py-2 focus:ring-4 focus:ring-white/30 focus:outline-none"
                    >
                      Profile
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        handleAccountSettings();
                        closeMenu();
                      }}
                      className="rounded-md px-4 py-2 focus:ring-4 focus:ring-white/30 focus:outline-none"
                    >
                      Settings
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        handleLogout();
                        closeMenu();
                      }}
                      className="rounded-md px-4 py-2 focus:ring-4 focus:ring-white/30 focus:outline-none"
                    >
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <button
                      onClick={() => {
                        handleLogin();
                        closeMenu();
                      }}
                      className="rounded-md px-4 py-2 focus:ring-4 focus:ring-white/30 focus:outline-none"
                    >
                      Login
                    </button>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}